import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Coins, TrendingUp, TrendingDown, Minus, RefreshCw, Loader2, Database, AlertTriangle, CalendarClock, Calendar, ShieldCheck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const METALS = [
  { key: "XAU", label: "Gold", color: "#FFB100" },
  { key: "XAG", label: "Silber", color: "#C0C0D0" },
  { key: "XPT", label: "Platin", color: "#E5E4E2" },
  { key: "XPD", label: "Palladium", color: "#CED0DD" },
  { key: "RATIO", label: "Gold/Silber-Ratio", color: "#C5993A", noCurrency: true, unit: "" },
  { key: "USD", label: "USD → EUR (Kurs)", color: "#4A90E2", forceCurrency: "EUR", unit: "EUR" },
];

const RANGES = [
  { key: "1D", label: "1 Tag" },
  { key: "1W", label: "1 Woche" },
  { key: "1M", label: "1 Monat" },
  { key: "1Y", label: "1 Jahr" },
  { key: "ALL", label: "Alles" },
];

const LEVEL_STYLE = {
  strong_buy:   { bg: "bg-emerald-500/15", border: "border-emerald-400/40", text: "text-emerald-300", icon: TrendingUp },
  buy:          { bg: "bg-emerald-500/10", border: "border-emerald-400/30", text: "text-emerald-200", icon: TrendingUp },
  neutral:      { bg: "bg-slate-500/15",   border: "border-slate-400/30",   text: "text-slate-200",   icon: Minus },
  sell:         { bg: "bg-rose-500/10",    border: "border-rose-400/30",    text: "text-rose-200",    icon: TrendingDown },
  strong_sell:  { bg: "bg-rose-500/15",    border: "border-rose-400/40",    text: "text-rose-300",    icon: TrendingDown },
};

const RISK_STYLE = {
  low:      { text: "text-emerald-300", bg: "bg-emerald-500/10", border: "border-emerald-400/30" },
  moderate: { text: "text-yellow-200",  bg: "bg-yellow-500/10",  border: "border-yellow-400/30" },
  elevated: { text: "text-orange-300",  bg: "bg-orange-500/10",  border: "border-orange-400/30" },
  high:     { text: "text-rose-300",    bg: "bg-rose-500/10",    border: "border-rose-400/30" },
  unknown:  { text: "text-slate-300",   bg: "bg-slate-500/10",   border: "border-slate-400/30" },
};

export default function AdminMetalsTab() {
  const [metal, setMetal] = useState("XAU");
  const [currency, setCurrency] = useState("EUR");
  const [range, setRange] = useState("1M");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [overview, setOverview] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [dailyFetching, setDailyFetching] = useState(false);
  const [customDate, setCustomDate] = useState("");
  const [customFetching, setCustomFetching] = useState(false);
  const [dupState, setDupState] = useState({ loading: false, count: null, deleted: null, error: "" });

  const metalCfg = METALS.find((m) => m.key === metal);
  const effectiveCurrency = metalCfg?.forceCurrency || currency;

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data: d } = await axios.get(`${API}/metals/prices`, {
        params: { metal, currency: effectiveCurrency, range },
        withCredentials: true,
      });
      setData(d);
    } catch (err) {
      setError(err.response?.data?.detail || "Konnte Daten nicht laden");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [metal, effectiveCurrency, range]);

  const loadOverview = useCallback(async () => {
    try {
      const { data: d } = await axios.get(`${API}/metals/overview`, { withCredentials: true });
      setOverview(d);
    } catch (err) { /* ignore */ }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadOverview(); }, [loadOverview]);

  const runImport = async () => {
    if (!window.confirm("Historische Kursdaten der letzten 5 Tage jetzt via metalpriceapi.com abrufen und speichern?")) return;
    setImporting(true);
    setImportResult(null);
    try {
      const { data: d } = await axios.post(`${API}/metals/import`, { days: 5 }, { withCredentials: true });
      setImportResult(d.result);
      await Promise.all([load(), loadOverview()]);
    } catch (err) {
      setImportResult({ error: err.response?.data?.detail || "Import fehlgeschlagen" });
    } finally {
      setImporting(false);
    }
  };

  const runDailyFetch = async () => {
    setDailyFetching(true);
    setImportResult(null);
    try {
      const { data: d } = await axios.post(`${API}/metals/daily-fetch`, {}, { withCredentials: true });
      // Present result in the same box as bootstrap import
      if (d.result?.ok) {
        setImportResult({ imported: [d.result.date], failed: [] });
      } else {
        setImportResult({ error: `Abruf für ${d.result?.date || "Vortag"} fehlgeschlagen` });
      }
      await Promise.all([load(), loadOverview()]);
    } catch (err) {
      setImportResult({ error: err.response?.data?.detail || "Abruf fehlgeschlagen" });
    } finally {
      setDailyFetching(false);
    }
  };

  const runCustomDateFetch = async () => {
    if (!customDate) return;
    setCustomFetching(true);
    setImportResult(null);
    try {
      const { data: d } = await axios.post(`${API}/metals/fetch-date`, { date: customDate }, { withCredentials: true });
      if (d.result?.ok) {
        setImportResult({ imported: [d.result.date], failed: [] });
      } else {
        setImportResult({ error: d.result?.error || d.message || "Abruf fehlgeschlagen" });
      }
      await Promise.all([load(), loadOverview()]);
    } catch (err) {
      setImportResult({ error: err.response?.data?.result?.error || err.response?.data?.message || err.response?.data?.detail || "Abruf fehlgeschlagen" });
    } finally {
      setCustomFetching(false);
    }
  };

  const checkDuplicates = async () => {
    setDupState({ loading: true, count: null, deleted: null, error: "" });
    try {
      const { data: d } = await axios.get(`${API}/metals/duplicates`, { withCredentials: true });
      setDupState({ loading: false, count: (d.duplicates || []).length, deleted: null, error: "" });
    } catch (err) {
      setDupState({ loading: false, count: null, deleted: null, error: err.response?.data?.detail || "Prüfung fehlgeschlagen" });
    }
  };

  const cleanupDuplicates = async () => {
    if (!window.confirm("Doppelte Datenbank-Einträge jetzt automatisch löschen? Der neueste Eintrag pro Tag/Metall wird behalten.")) return;
    setDupState((s) => ({ ...s, loading: true, error: "" }));
    try {
      const { data: d } = await axios.post(`${API}/metals/cleanup-duplicates`, {}, { withCredentials: true });
      setDupState({ loading: false, count: 0, deleted: d.deleted ?? 0, error: "" });
      await Promise.all([load(), loadOverview()]);
    } catch (err) {
      setDupState((s) => ({ ...s, loading: false, error: err.response?.data?.detail || "Bereinigung fehlgeschlagen" }));
    }
  };

  const score = data?.score;
  const level = score?.available ? score.level : null;
  const styleCfg = level ? LEVEL_STYLE[level] : null;
  const StyleIcon = styleCfg?.icon || Minus;
  const riskCfg = score?.available ? RISK_STYLE[score.risk_level] || RISK_STYLE.unknown : RISK_STYLE.unknown;

  const chartColor = metalCfg?.color || "#C5993A";
  const unit = metalCfg?.unit ?? effectiveCurrency;
  const formatValue = (v) => {
    if (typeof v !== "number") return v;
    const digits = metal === "RATIO" ? 2 : (v < 10 ? 4 : 2);
    return v.toLocaleString("de-DE", { minimumFractionDigits: digits, maximumFractionDigits: digits });
  };

  return (
    <div data-testid="admin-metals-tab">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h3 className="font-heading text-xl text-white flex items-center gap-2">
          <Coins className="w-5 h-5 text-[#C5993A]" /> Edelmetalle & Kurse
        </h3>
        <div className="flex items-center gap-2">
          <Button
            onClick={load}
            variant="ghost"
            className="text-[#C5993A] hover:bg-[#C5993A]/10 text-xs"
            data-testid="metals-refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} /> Neu laden
          </Button>
          <Button
            onClick={runDailyFetch}
            disabled={dailyFetching}
            className="bg-[#0C1620] border border-[#C5993A]/40 text-[#C5993A] hover:bg-[#C5993A]/10 text-xs"
            data-testid="metals-daily-fetch-btn"
          >
            {dailyFetching ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Abruf…</> : <><CalendarClock className="w-4 h-4 mr-2" /> Tageskurs abrufen</>}
          </Button>
          <div className="flex items-center gap-1 bg-[#0C1620] border border-[#3A4F60]/40 rounded-md px-1.5 py-0.5">
            <Calendar className="w-3.5 h-3.5 text-[#C5993A] ml-1" />
            <input
              type="date"
              value={customDate}
              max={new Date(Date.now() - 86400000).toISOString().slice(0, 10)}
              onChange={(e) => setCustomDate(e.target.value)}
              data-testid="metals-custom-date-input"
              className="bg-transparent text-xs text-[#F0F4F8] focus:outline-none [color-scheme:dark] py-1"
            />
            <Button
              onClick={runCustomDateFetch}
              disabled={!customDate || customFetching}
              data-testid="metals-custom-date-fetch-btn"
              className="bg-[#C5993A]/15 hover:bg-[#C5993A]/25 text-[#C5993A] text-[10px] px-2 py-1 h-auto border-0"
              title="Kurs für dieses Datum abrufen und speichern"
            >
              {customFetching ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Abrufen"}
            </Button>
          </div>
          <Button
            onClick={runImport}
            disabled={importing}
            className="bg-gradient-to-r from-[#C5993A] to-[#8B6B20] text-white border-0 text-xs"
            data-testid="metals-import-btn"
          >
            {importing ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Import…</> : <><Database className="w-4 h-4 mr-2" /> 5 Tage importieren</>}
          </Button>
        </div>
      </div>

      {/* Overview + import result */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
        <StatCard label="Neuester Datenpunkt" value={overview?.latest_date || "—"} />
        <StatCard label="Datenzeilen" value={overview?.total_rows ?? "—"} />
        <StatCard label="Erfasste Tage" value={overview?.distinct_days ?? "—"} />
      </div>
      {importResult && (
        <div className="bg-[#12202f] border border-[#3A4F60]/30 rounded-lg p-3 mb-6 text-xs text-[#F0F4F8]" data-testid="metals-import-result">
          {importResult.error ? (
            <span className="text-rose-300">Fehler: {importResult.error}</span>
          ) : (
            <>
              <span className="text-emerald-300">✓ Import abgeschlossen.</span>{" "}
              Gespeichert: <b>{importResult.imported?.length || 0}</b> Tage
              {(importResult.failed?.length ?? 0) > 0 && (<> · Fehlgeschlagen: <b className="text-rose-300">{importResult.failed.length}</b> ({importResult.failed.join(", ")})</>)}
            </>
          )}
        </div>
      )}

      {/* Selectors */}
      <div className="bg-[#12202f] border border-[#3A4F60]/30 rounded-lg p-4 mb-6">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto_auto] gap-4">
          <div>
            <p className="text-[10px] uppercase tracking-wider text-[#8899AA] mb-2">Metall</p>
            <div className="flex flex-wrap gap-1.5" data-testid="metals-selector">
              {METALS.map((m) => (
                <button
                  key={m.key}
                  onClick={() => setMetal(m.key)}
                  data-testid={`metal-${m.key}-btn`}
                  className={`px-3 py-1.5 rounded-md text-xs transition-colors ${
                    metal === m.key
                      ? "bg-[#C5993A] text-[#0C1620] font-medium"
                      : "bg-[#0C1620] text-[#F0F4F8] border border-[#3A4F60]/40 hover:border-[#C5993A]/40"
                  }`}
                >{m.label}</button>
              ))}
            </div>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wider text-[#8899AA] mb-2">Währung</p>
            <div className="flex gap-1.5" data-testid="currency-selector">
              {["EUR", "USD"].map((c) => (
                <button
                  key={c}
                  onClick={() => setCurrency(c)}
                  disabled={metalCfg?.forceCurrency || metalCfg?.noCurrency}
                  data-testid={`currency-${c}-btn`}
                  className={`px-3 py-1.5 rounded-md text-xs transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                    effectiveCurrency === c
                      ? "bg-[#C5993A] text-[#0C1620] font-medium"
                      : "bg-[#0C1620] text-[#F0F4F8] border border-[#3A4F60]/40 hover:border-[#C5993A]/40"
                  }`}
                >{c}</button>
              ))}
            </div>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wider text-[#8899AA] mb-2">Zeitraum</p>
            <div className="flex gap-1.5" data-testid="range-selector">
              {RANGES.map((r) => (
                <button
                  key={r.key}
                  onClick={() => setRange(r.key)}
                  data-testid={`range-${r.key}-btn`}
                  className={`px-2.5 py-1.5 rounded-md text-xs transition-colors ${
                    range === r.key
                      ? "bg-[#C5993A] text-[#0C1620] font-medium"
                      : "bg-[#0C1620] text-[#F0F4F8] border border-[#3A4F60]/40 hover:border-[#C5993A]/40"
                  }`}
                >{r.label}</button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Chart + Score */}
      <div className="grid grid-cols-1 xl:grid-cols-[1.7fr_1fr] gap-4">
        {/* Chart */}
        <div className="bg-[#12202f] border border-[#3A4F60]/30 rounded-lg p-4">
          <div className="flex items-baseline justify-between mb-3">
            <div>
              <p className="text-[10px] uppercase tracking-wider text-[#8899AA]">Kursverlauf</p>
              <h4 className="font-heading text-lg text-white">{data?.metal_label || metalCfg?.label}</h4>
            </div>
            {data?.series?.length > 0 && (
              <p className="text-xs text-[#8899AA]">
                {data.series.length} Datenpunkt{data.series.length !== 1 && "e"}
              </p>
            )}
          </div>
          {loading ? (
            <div className="h-72 flex items-center justify-center"><Loader2 className="w-6 h-6 text-[#C5993A] animate-spin" /></div>
          ) : error ? (
            <div className="h-72 flex items-center justify-center text-rose-300 text-sm">{error}</div>
          ) : !data?.series || data.series.length === 0 ? (
            <div className="h-72 flex flex-col items-center justify-center gap-2 text-[#8899AA] text-sm">
              <AlertTriangle className="w-8 h-8 text-[#C5993A]/60" />
              <p>Noch keine Daten gespeichert.</p>
              <p className="text-xs">Klicke oben auf <b>{"„5 Tage importieren"}</b>, um zu starten.</p>
            </div>
          ) : (
            <div style={{ width: "100%", height: 300 }}>
              <ResponsiveContainer>
                <LineChart data={data.series} margin={{ top: 8, right: 12, bottom: 8, left: 12 }}>
                  <CartesianGrid stroke="#3A4F60" strokeDasharray="3 3" opacity={0.3} />
                  <XAxis
                    dataKey="date"
                    stroke="#8899AA"
                    fontSize={10}
                    tickFormatter={(d) => {
                      const [y, m, day] = d.split("-");
                      return `${day}.${m}`;
                    }}
                  />
                  <YAxis stroke="#8899AA" fontSize={10} domain={["auto", "auto"]} tickFormatter={formatValue} />
                  <Tooltip
                    contentStyle={{ background: "#0C1620", border: "1px solid #3A4F60", borderRadius: 6, fontSize: 12 }}
                    labelStyle={{ color: "#C5993A" }}
                    itemStyle={{ color: "#F0F4F8" }}
                    formatter={(v) => [`${formatValue(v)} ${unit}`, data.metal_label]}
                    labelFormatter={(d) => new Date(d).toLocaleDateString("de-DE", { weekday: "short", day: "2-digit", month: "2-digit", year: "numeric" })}
                  />
                  <Line
                    type="monotone"
                    dataKey="value"
                    stroke={chartColor}
                    strokeWidth={2}
                    dot={data.series.length <= 15 ? { r: 3, fill: chartColor } : false}
                    activeDot={{ r: 5 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        {/* Score / Recommendation */}
        <div className="bg-[#12202f] border border-[#3A4F60]/30 rounded-lg p-4" data-testid="metals-score-panel">
          <p className="text-[10px] uppercase tracking-wider text-[#8899AA] mb-2">Bewertung</p>
          {!score ? (
            <div className="text-[#8899AA] text-sm py-4">Keine Bewertung verfügbar</div>
          ) : !score.available ? (
            <div className="text-[#8899AA] text-sm py-4">{score.reason}</div>
          ) : (
            <>
              {/* Recommendation */}
              <div className={`rounded-lg border p-4 mb-3 ${styleCfg.bg} ${styleCfg.border}`}>
                <div className="flex items-center gap-2 mb-1">
                  <StyleIcon className={`w-5 h-5 ${styleCfg.text}`} />
                  <h4 className={`font-heading text-lg ${styleCfg.text}`} data-testid="recommendation-label">{score.label}</h4>
                </div>
                <p className="text-xs text-[#8899AA]">Kombinierter Score: <b className={styleCfg.text}>{score.total_score > 0 ? `+${score.total_score}` : score.total_score}</b></p>
              </div>

              {/* Risk */}
              <div className={`rounded-lg border p-3 mb-3 ${riskCfg.bg} ${riskCfg.border}`}>
                <div className="flex items-center justify-between">
                  <span className="text-[10px] uppercase tracking-wider text-[#8899AA]">Risiko</span>
                  <span className={`font-medium text-sm ${riskCfg.text}`} data-testid="risk-label">{score.risk_label}</span>
                </div>
                {score.metrics?.volatility_pct !== null && (
                  <p className="text-[10px] text-[#8899AA] mt-1">Volatilität (30T): {score.metrics.volatility_pct}%</p>
                )}
              </div>

              {/* Reasons */}
              <div className="mb-3">
                <p className="text-[10px] uppercase tracking-wider text-[#8899AA] mb-2">Begründung</p>
                <ul className="space-y-1.5" data-testid="score-reasons">
                  {score.reasons.map((r, i) => (
                    <li key={i} className="text-xs text-[#F0F4F8] flex gap-2">
                      <span className="text-[#C5993A]">›</span>
                      <span>{r}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Metrics grid */}
              <div className="grid grid-cols-2 gap-2 pt-3 border-t border-[#3A4F60]/30">
                <MetricMini label="Preis" value={`${formatValue(score.metrics.last_price)} ${unit}`} />
                <MetricMini label="Trend 30T" value={`${score.metrics.trend_pct_30d > 0 ? "+" : ""}${score.metrics.trend_pct_30d}%`} />
                {score.metrics.rsi !== null && <MetricMini label="RSI" value={score.metrics.rsi} />}
                {score.metrics.ratio !== null && <MetricMini label="G/S-Ratio" value={score.metrics.ratio} />}
                {score.metrics.sma50 !== null && <MetricMini label="SMA50" value={formatValue(score.metrics.sma50)} />}
                {score.metrics.sma200 !== null && <MetricMini label="SMA200" value={formatValue(score.metrics.sma200)} />}
              </div>

              <p className="text-[10px] text-[#8899AA] mt-3 italic">
                Basierend auf {score.data_points} Datenpunkt{score.data_points !== 1 && "en"}. Keine Anlageberatung.
              </p>
            </>
          )}
        </div>
      </div>

      {/* Cron info */}
      <div className="mt-6 bg-[#12202f] border border-[#3A4F60]/30 rounded-lg p-4 text-xs text-[#8899AA]">
        <p className="text-[#C5993A] font-medium mb-1">Täglicher Kurs-Import (Cron)</p>
        <p>Um jeden Tag automatisch die aktuellen Kurse zu holen, legen Sie im All-Inkl KAS-Adminbereich einen Cronjob an, der einmal täglich (z. B. 07:00 Uhr) folgende URL aufruft:</p>
        <code className="block mt-2 p-2 bg-[#0C1620] border border-[#3A4F60]/40 rounded text-[10px] text-[#F0F4F8] break-all">
          https://vmbs.de/api/metals/daily-cron?token=&lt;METALS_CRON_TOKEN&gt;
        </code>
        <p className="mt-2">Der Token befindet sich in <code>api/.env</code> (Feld <code>METALS_CRON_TOKEN</code>).</p>
      </div>

      {/* Duplicate check / cleanup */}
      <div className="mt-4 bg-[#12202f] border border-[#3A4F60]/30 rounded-lg p-4" data-testid="metals-dup-panel">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-[#C5993A]" />
            <div>
              <p className="text-sm text-white font-medium">Datenbank-Integrität</p>
              <p className="text-[10px] text-[#8899AA]">Prüfen &amp; bereinigen — pro Tag/Metall soll nur ein Eintrag existieren</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={checkDuplicates}
              disabled={dupState.loading}
              data-testid="metals-dup-check-btn"
              className="bg-[#0C1620] border border-[#3A4F60]/40 text-[#F0F4F8] hover:border-[#C5993A]/40 text-xs"
            >
              {dupState.loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Prüfe…</> : "Prüfen"}
            </Button>
            <Button
              onClick={cleanupDuplicates}
              disabled={dupState.loading || dupState.count === 0}
              data-testid="metals-dup-cleanup-btn"
              className="bg-[#C5993A]/15 border border-[#C5993A]/40 text-[#C5993A] hover:bg-[#C5993A]/25 text-xs"
            >
              Duplikate löschen
            </Button>
          </div>
        </div>
        {(dupState.count !== null || dupState.deleted !== null || dupState.error) && (
          <div className="mt-3 text-xs" data-testid="metals-dup-result">
            {dupState.error && <p className="text-rose-300">{dupState.error}</p>}
            {dupState.deleted !== null && (
              <p className="text-emerald-300">
                ✓ Bereinigung abgeschlossen — {dupState.deleted} Duplikat{dupState.deleted !== 1 && "e"} gelöscht.
              </p>
            )}
            {dupState.deleted === null && dupState.count !== null && (
              dupState.count === 0
                ? <p className="text-emerald-300">✓ Keine Duplikate gefunden. Datenbank ist sauber.</p>
                : <p className="text-yellow-200">⚠ {dupState.count} Duplikat{dupState.count !== 1 && "e"} gefunden. Nutze &bdquo;Duplikate löschen&ldquo;, um zu bereinigen.</p>
            )}
          </div>
        )}
      </div>

      {/* Data table */}
      <PricesTable currency={effectiveCurrency} />
    </div>
  );
}

function PricesTable({ currency }) {
  const [limit, setLimit] = useState(20);
  const [date, setDate] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = { currency };
      if (date) params.date = date;
      else params.limit = limit;
      const { data: d } = await axios.get(`${API}/metals/table`, { params, withCredentials: true });
      setRows(d.rows || []);
    } catch (err) {
      setError(err.response?.data?.detail || "Konnte Tabelle nicht laden");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [limit, date, currency]);

  useEffect(() => { load(); }, [load]);

  const fmt = (v, digits = 2) => v == null ? "—" : v.toLocaleString("de-DE", { minimumFractionDigits: digits, maximumFractionDigits: digits });
  const cUnit = currency;
  const resetDate = () => { setDate(""); };

  return (
    <div className="mt-6 bg-[#12202f] border border-[#3A4F60]/30 rounded-lg p-4" data-testid="prices-table-wrap">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <p className="text-[10px] uppercase tracking-wider text-[#8899AA]">Alle Datenbankeinträge</p>
          <h4 className="font-heading text-lg text-white">Historische Kursdaten ({cUnit})</h4>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex gap-1.5" data-testid="table-limit-selector">
            {[20, 50, 100].map((n) => (
              <button
                key={n}
                onClick={() => { setDate(""); setLimit(n); }}
                data-testid={`table-limit-${n}-btn`}
                className={`px-3 py-1.5 rounded-md text-xs transition-colors ${
                  !date && limit === n
                    ? "bg-[#C5993A] text-[#0C1620] font-medium"
                    : "bg-[#0C1620] text-[#F0F4F8] border border-[#3A4F60]/40 hover:border-[#C5993A]/40"
                }`}
              >{n} Tage</button>
            ))}
          </div>
          <div className="flex items-center gap-1.5">
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              data-testid="table-date-picker"
              className="px-2.5 py-1.5 rounded-md text-xs bg-[#0C1620] text-[#F0F4F8] border border-[#3A4F60]/40 focus:border-[#C5993A]/60 focus:outline-none [color-scheme:dark]"
              title="Bestimmtes Datum suchen"
            />
            {date && (
              <button
                onClick={resetDate}
                data-testid="table-date-reset-btn"
                className="px-2 py-1.5 rounded-md text-xs bg-[#0C1620] text-[#C5993A] border border-[#C5993A]/40 hover:bg-[#C5993A]/10"
                title="Datumsfilter zurücksetzen"
              >
                ✕
              </button>
            )}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="py-8 flex items-center justify-center"><Loader2 className="w-5 h-5 text-[#C5993A] animate-spin" /></div>
      ) : error ? (
        <div className="py-6 text-rose-300 text-sm text-center">{error}</div>
      ) : rows.length === 0 ? (
        <div className="py-6 text-[#8899AA] text-sm text-center">
          {date ? `Keine Daten für ${date} gefunden.` : "Noch keine Datenbankeinträge vorhanden."}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="prices-table">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-wider text-[#8899AA] border-b border-[#3A4F60]/40">
                <th className="py-2 pr-3">Datum</th>
                <th className="py-2 pr-3 text-right">Gold ({cUnit})</th>
                <th className="py-2 pr-3 text-right">Silber ({cUnit})</th>
                <th className="py-2 pr-3 text-right">Platin ({cUnit})</th>
                <th className="py-2 pr-3 text-right">Palladium ({cUnit})</th>
                <th className="py-2 pr-3 text-right">USD → EUR</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr
                  key={r.date}
                  data-testid={`price-row-${r.date}`}
                  className="border-b border-[#3A4F60]/20 hover:bg-[#0C1620]/50 transition-colors"
                >
                  <td className="py-2 pr-3 text-[#F0F4F8] font-medium">
                    {new Date(r.date + "T00:00:00").toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" })}
                  </td>
                  <td className="py-2 pr-3 text-right text-[#FFB100] tabular-nums">{fmt(r.xau, 2)}</td>
                  <td className="py-2 pr-3 text-right text-[#C0C0D0] tabular-nums">{fmt(r.xag, 4)}</td>
                  <td className="py-2 pr-3 text-right text-[#E5E4E2] tabular-nums">{fmt(r.xpt, 2)}</td>
                  <td className="py-2 pr-3 text-right text-[#CED0DD] tabular-nums">{fmt(r.xpd, 2)}</td>
                  <td className="py-2 pr-3 text-right text-[#4A90E2] tabular-nums">{fmt(r.usd_eur, 4)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="text-[10px] text-[#8899AA] mt-3 text-right">
            {date ? `Datum: ${date}` : `Zeige ${rows.length} Tag${rows.length !== 1 ? "e" : ""} (neueste zuerst)`}
          </p>
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value }) {
  return (
    <div className="bg-[#12202f] border border-[#3A4F60]/30 rounded-lg p-3">
      <p className="text-[10px] uppercase tracking-wider text-[#8899AA]">{label}</p>
      <p className="text-lg font-heading text-white mt-1">{value}</p>
    </div>
  );
}

function MetricMini({ label, value }) {
  return (
    <div>
      <p className="text-[9px] uppercase tracking-wider text-[#8899AA]">{label}</p>
      <p className="text-xs text-[#F0F4F8] font-medium">{value}</p>
    </div>
  );
}
